import React, { useEffect, useState } from 'react';
import { KeyRound, RefreshCw, Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../supabaseClient';
import { useAuthStore } from '../store/useAuthStore';
import { generatePassword, MIN_PASSWORD } from '../utils/businessLogin';
import { changeMyPassword, hasPasswordLogin } from '../utils/accountPassword';

const inputClass = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2.5 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green font-mono';

// «Mi cuenta»: cada persona (administrador o negocio) crea o cambia su propia contraseña. El administrador, además, crea o cambia la de un negocio en Administrar Clientes.
export default function AccountPage() {
  const { currentUserEmail } = useAuthStore();
  const [user, setUser] = useState(undefined); // undefined = cargando, null = sin sesión de Supabase (p. ej. cuentas demo)
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    supabase.auth.getUser().then(({ data }) => { if (alive) setUser(data?.user ?? null); }).catch(() => { if (alive) setUser(null); });
    return () => { alive = false; };
  }, []);

  if (user === undefined) return <p className="text-xs text-seryi-text">Cargando…</p>;

  const email = user?.email || currentUserEmail;
  const needsCurrent = hasPasswordLogin(user);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    const result = await changeMyPassword({ email, current, next, confirm, needsCurrent });
    setBusy(false);
    if (!result.ok) { setError(result.error); return; }
    toast.success(result.created ? 'Contraseña creada. Ya puedes entrar con tu correo y contraseña.' : 'Contraseña actualizada.');
    setCurrent(''); setNext(''); setConfirm('');
    if (result.created) setUser((u) => ({ ...u, app_metadata: { ...u.app_metadata, providers: [...(u.app_metadata?.providers || []), 'email'] } }));
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h2 className="text-xl font-extrabold text-seryi-heading">Mi cuenta</h2>
        <p className="text-xs text-seryi-text">Tu acceso a Keyla CRM.</p>
      </div>

      <div className="bg-seryi-bg border border-seryi-bg-light p-6 rounded-3xl space-y-4">
        <p className="text-[10px] uppercase font-bold text-seryi-text">Correo de acceso</p>
        <p className="text-sm font-bold text-seryi-heading break-all">{email || '—'}</p>

        {!user ? (
          <p className="text-xs text-seryi-text">Esta cuenta no tiene contraseña propia (por ejemplo, las cuentas de demostración). Entra con tu cuenta real para crearla o cambiarla.</p>
        ) : (
          <form onSubmit={submit} className="space-y-3" aria-label={needsCurrent ? 'Cambiar contraseña' : 'Crear contraseña'}>
            <p className="text-xs font-bold text-seryi-heading flex items-center gap-1.5">
              <KeyRound size={14} /> {needsCurrent ? 'Cambiar contraseña' : 'Crear contraseña'}
            </p>
            {!needsCurrent && <p className="text-[11px] text-seryi-text">Hoy entras con Google. Si creas una contraseña, también podrás entrar con tu correo y contraseña.</p>}

            {needsCurrent && (
              <div className="space-y-1">
                <label htmlFor="acc-current" className="text-[10px] uppercase text-seryi-text">Contraseña actual</label>
                <input id="acc-current" type={visible ? 'text' : 'password'} autoComplete="current-password" className={inputClass} value={current} onChange={(e) => setCurrent(e.target.value)} />
              </div>
            )}

            <div className="space-y-1">
              <label htmlFor="acc-next" className="text-[10px] uppercase text-seryi-text">Contraseña nueva (mín. {MIN_PASSWORD})</label>
              <div className="flex gap-1.5">
                <div className="relative flex-1">
                  <input id="acc-next" type={visible ? 'text' : 'password'} autoComplete="new-password" className={`${inputClass} pr-8`} value={next} onChange={(e) => setNext(e.target.value)} />
                  <button type="button" onClick={() => setVisible((v) => !v)} aria-label={visible ? 'Ocultar contraseñas' : 'Mostrar contraseñas'} className="absolute right-2 top-2.5 text-seryi-text hover:text-seryi-heading">
                    {visible ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                <button type="button" onClick={() => { const p = generatePassword(); setNext(p); setConfirm(p); setVisible(true); }} aria-label="Generar contraseña" title="Generar una contraseña segura" className="px-2.5 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl text-seryi-text hover:text-seryi-green">
                  <RefreshCw size={14} />
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <label htmlFor="acc-confirm" className="text-[10px] uppercase text-seryi-text">Repite la contraseña nueva</label>
              <input id="acc-confirm" type={visible ? 'text' : 'password'} autoComplete="new-password" className={inputClass} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>

            {error && <p role="alert" className="text-xs text-rose-400">{error}</p>}

            <button type="submit" disabled={busy} className="w-full bg-seryi-green text-seryi-bg-dark font-black py-2.5 rounded-xl text-xs flex items-center justify-center gap-2 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} {needsCurrent ? 'Guardar contraseña nueva' : 'Crear contraseña'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
